export type AudioSegment = {
  index: number;
  blob: Blob;
  startSeconds: number;
  endSeconds: number;
  durationSeconds: number;
};

export const TARGET_SAMPLE_RATE = 16_000;
const FRAME_SECONDS = 0.1;
const SILENCE_THRESHOLD_RMS = 0.012;
const SPLIT_SILENCE_SECONDS = 3;
const MAX_KEPT_SILENCE_SECONDS = 2;

type Range = { start: number; end: number };

export async function prepareTranscriptionSegments(blob: Blob): Promise<AudioSegment[]> {
  const audioBuffer = await decodeAudio(blob);
  const mono = mixToMono(audioBuffer);
  const samples = resampleLinear(mono, audioBuffer.sampleRate, TARGET_SAMPLE_RATE);
  const ranges = buildSegmentRanges(samples, TARGET_SAMPLE_RATE);

  return ranges.map((range, index) => {
    const segmentSamples = compactSilence(
      samples.subarray(range.start, range.end),
      TARGET_SAMPLE_RATE,
    );
    const wav = encodeWav(segmentSamples, TARGET_SAMPLE_RATE);
    return {
      index,
      blob: new Blob([wav], { type: "audio/wav" }),
      startSeconds: range.start / TARGET_SAMPLE_RATE,
      endSeconds: range.end / TARGET_SAMPLE_RATE,
      durationSeconds: segmentSamples.length / TARGET_SAMPLE_RATE,
    };
  });
}

export function preparePcmTranscriptionSegment(
  input: Float32Array,
  sampleRate: number,
  index: number,
): AudioSegment | null {
  const resampled = resampleLinear(input, sampleRate, TARGET_SAMPLE_RATE);
  const compacted = compactSilence(resampled, TARGET_SAMPLE_RATE);
  if (compacted.length < TARGET_SAMPLE_RATE * 0.35) return null;

  const wav = encodeWav(compacted, TARGET_SAMPLE_RATE);
  return {
    index,
    blob: new Blob([wav], { type: "audio/wav" }),
    startSeconds: 0,
    endSeconds: input.length / sampleRate,
    durationSeconds: compacted.length / TARGET_SAMPLE_RATE,
  };
}

async function decodeAudio(blob: Blob): Promise<AudioBuffer> {
  const ctx = new AudioContext();
  try {
    return await ctx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    await ctx.close();
  }
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  const out = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i++) out[i]! += data[i]! / buffer.numberOfChannels;
  }
  return out;
}

function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const t = src - i0;
    out[i] = input[i0]! * (1 - t) + input[i1]! * t;
  }
  return out;
}

function buildSegmentRanges(samples: Float32Array, sampleRate: number): Range[] {
  const frameSize = Math.max(1, Math.floor(FRAME_SECONDS * sampleRate));
  const splitFrames = Math.ceil(SPLIT_SILENCE_SECONDS / FRAME_SECONDS);
  const frames = Math.ceil(samples.length / frameSize);
  const silent = Array.from({ length: frames }, (_, frame) => {
    const start = frame * frameSize;
    const end = Math.min(samples.length, start + frameSize);
    return rms(samples.subarray(start, end)) < SILENCE_THRESHOLD_RMS;
  });

  const ranges: Range[] = [];
  let segmentStartFrame = 0;
  let silenceStartFrame: number | null = null;

  for (let frame = 0; frame <= silent.length; frame++) {
    const isSilent = silent[frame] ?? false;
    if (isSilent && silenceStartFrame === null) silenceStartFrame = frame;
    if (!isSilent && silenceStartFrame !== null) {
      const silenceLength = frame - silenceStartFrame;
      if (silenceLength >= splitFrames) {
        const endFrame = silenceStartFrame;
        pushRange(ranges, segmentStartFrame * frameSize, endFrame * frameSize, samples.length);
        segmentStartFrame = frame;
      }
      silenceStartFrame = null;
    }
  }

  pushRange(ranges, segmentStartFrame * frameSize, samples.length, samples.length);
  return ranges.length > 0 ? ranges : [{ start: 0, end: samples.length }];
}

function pushRange(ranges: Range[], start: number, end: number, max: number) {
  const clampedStart = Math.max(0, Math.min(start, max));
  const clampedEnd = Math.max(clampedStart, Math.min(end, max));
  if (clampedEnd - clampedStart > TARGET_SAMPLE_RATE * 0.5) {
    ranges.push({ start: clampedStart, end: clampedEnd });
  }
}

export function compactSilence(samples: Float32Array, sampleRate: number): Float32Array {
  const frameSize = Math.max(1, Math.floor(FRAME_SECONDS * sampleRate));
  const maxSilentFrames = Math.ceil(MAX_KEPT_SILENCE_SECONDS / FRAME_SECONDS);
  const chunks: Float32Array[] = [];
  let silenceStart: number | null = null;

  const flushSilence = (endFrame: number) => {
    if (silenceStart === null) return;
    const keepFrames = Math.min(endFrame - silenceStart, maxSilentFrames);
    const keepStartFrame = Math.max(silenceStart, endFrame - keepFrames);
    chunks.push(samples.subarray(keepStartFrame * frameSize, Math.min(samples.length, endFrame * frameSize)));
    silenceStart = null;
  };

  const frameCount = Math.ceil(samples.length / frameSize);
  for (let frame = 0; frame < frameCount; frame++) {
    const start = frame * frameSize;
    const end = Math.min(samples.length, start + frameSize);
    const isSilent = rms(samples.subarray(start, end)) < SILENCE_THRESHOLD_RMS;
    if (isSilent) {
      if (silenceStart === null) silenceStart = frame;
      continue;
    }
    flushSilence(frame);
    chunks.push(samples.subarray(start, end));
  }
  flushSilence(frameCount);

  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return Math.sqrt(sum / samples.length);
}

export function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const bytesPerSample = 2;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += bytesPerSample;
  }
  return buffer;
}

function writeAscii(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}
